import { useEffect, useState } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { KPIsWidgetEnum, LongTitles } from '../utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/inputs/Button';
import { showException } from 'utils/exception-helper';
import { downloadBlob } from 'utils/file-utils';
import { formatDate } from 'utils/date-helpers';
import { useKPIGetDeviationJobsByBooker } from 'api/kpi/kpi';
import { useExportExportDeviationJobsHook } from 'api/export/export';
import { KPIDeviationJobDataPoint, KPIGroupBy } from 'api/model';
import { maxKPIDateRangeMs } from 'constants/AppConstants';

type SortField =
  | 'groupName'
  | 'totalCases'
  | 'deviationCases'
  | 'deviationPercentage';
type SortDirection = 'asc' | 'desc';

const SortIndicator = ({
  field,
  sortField,
  sortDirection,
}: {
  field: SortField;
  sortField: SortField;
  sortDirection: SortDirection;
}) => {
  if (sortField !== field) return null;
  return <span>{sortDirection === 'asc' ? ' \u25B2' : ' \u25BC'}</span>;
};

const DeviationJobsWidget = () => {
  const [groupBy, setGroupBy] = useState<KPIGroupBy>(KPIGroupBy.Booker);
  const [sortField, setSortField] = useState<SortField>('deviationPercentage');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    dashboardState: {
      daterange,
      selectedSupplierIDs,
      selectedCustomerNames,
      selectedFromCountyIDs,
      selectedToCountyIDs,
      excludeCompanyNameContains,
    },
  } = useDashboardControls();

  const getKPIDeviationData = useKPIGetDeviationJobsByBooker();

  const startDate = daterange[0];
  const endDate = daterange[1];
  const startDateTime = startDate ? startDate.getTime() : null;
  const endDateTime = endDate ? endDate.getTime() : null;

  const isDateRangeTooLong =
    startDate &&
    endDate &&
    endDate.getTime() - startDate.getTime() > maxKPIDateRangeMs;

  useEffect(() => {
    if (startDate && endDate && !isDateRangeTooLong) {
      getKPIDeviationData.mutate({
        data: {
          startDate,
          endDate,
          supplierIDs: selectedSupplierIDs,
          customerNames: selectedCustomerNames,
          fromCountyIDs: selectedFromCountyIDs,
          toCountyIDs: selectedToCountyIDs,
          kpiGroupBy: groupBy,
          excludeCompanyNameContains: excludeCompanyNameContains ?? undefined,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    startDateTime,
    endDateTime,
    selectedSupplierIDs,
    selectedCustomerNames,
    selectedFromCountyIDs,
    selectedToCountyIDs,
    groupBy,
    excludeCompanyNameContains,
    isDateRangeTooLong,
  ]);

  const downloadExcel = useExportExportDeviationJobsHook();

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortData = (
    data: KPIDeviationJobDataPoint[]
  ): KPIDeviationJobDataPoint[] => {
    return [...data].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;

      switch (sortField) {
        case 'groupName':
          aVal = a.groupName || '';
          bVal = b.groupName || '';
          break;
        case 'totalCases':
          aVal = a.totalCases;
          bVal = b.totalCases;
          break;
        case 'deviationCases':
          aVal = a.deviationCases;
          bVal = b.deviationCases;
          break;
        case 'deviationPercentage':
          aVal = a.deviationPercentage;
          bVal = b.deviationPercentage;
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string') {
        const comparison = aVal.localeCompare(bVal as string);
        return sortDirection === 'asc' ? comparison : -comparison;
      }
      return sortDirection === 'asc'
        ? aVal - (bVal as number)
        : (bVal as number) - aVal;
    });
  };

  const currentData = getKPIDeviationData.data?.dataPoints || [];
  const sortedData = sortData(currentData);

  const groupLabel = groupBy === KPIGroupBy.Company ? 'Företag' : 'Säljare';

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.DeviationJobs]}
      count={undefined}
      isLoading={getKPIDeviationData.isLoading}
      info="Visar totalt antal ärenden och andel avvikande jobb (RMÖvrigt). Baserat på ärendets stängd-datum."
    >
      <p style={{ fontWeight: 'bold', textAlign: 'center' }}>
        OBS: Datumen utgår från ärendets stängd-datum.
      </p>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
        <Button
          disabled={
            isDownloading || (!isDateRangeTooLong && currentData.length === 0)
          }
          onClick={async () => {
            try {
              setIsDownloading(true);
              const result = await downloadExcel({
                startDate: daterange[0]!,
                endDate: daterange[1]!,
                supplierIDs: selectedSupplierIDs,
                customerNames: selectedCustomerNames,
                fromCountyIDs: selectedFromCountyIDs,
                toCountyIDs: selectedToCountyIDs,
                kpiGroupBy: groupBy,
                excludeCompanyNameContains:
                  excludeCompanyNameContains ?? undefined,
              });
              if (result) {
                downloadBlob(
                  result,
                  `Nyckeltal_AvvikandeJobb_${formatDate(daterange[0]!)}_${formatDate(daterange[1]!)}.xlsx`
                );
              }
            } catch (error) {
              showException(error);
            } finally {
              setIsDownloading(false);
            }
          }}
        >
          <FontAwesomeIcon icon={faDownload} /> Ladda ner
          {isDownloading ? (
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              style={{ marginLeft: '5px' }}
            />
          ) : null}
        </Button>
      </div>

      <div style={{ display: 'flex', marginBottom: '15px' }}>
        <button
          type="button"
          onClick={() => setGroupBy(KPIGroupBy.Booker)}
          style={{
            padding: '8px 16px',
            cursor: 'pointer',
            backgroundColor:
              groupBy === KPIGroupBy.Booker ? '#007bff' : '#f0f0f0',
            color: groupBy === KPIGroupBy.Booker ? 'white' : 'black',
            border: '1px solid #ccc',
            borderRadius: '4px 0 0 4px',
          }}
        >
          Per säljare
        </button>
        <button
          type="button"
          onClick={() => setGroupBy(KPIGroupBy.Company)}
          style={{
            padding: '8px 16px',
            cursor: 'pointer',
            backgroundColor:
              groupBy === KPIGroupBy.Company ? '#007bff' : '#f0f0f0',
            color: groupBy === KPIGroupBy.Company ? 'white' : 'black',
            border: '1px solid #ccc',
            borderLeft: 'none',
            borderRadius: '0 4px 4px 0',
          }}
        >
          Per företag
        </button>
      </div>

      {isDateRangeTooLong ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p>
            Datumintervallet är för långt för att visa data. Använd
            nedladdningsknappen för att exportera data.
          </p>
        </div>
      ) : sortedData.length === 0 ? (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          {getKPIDeviationData.isLoading ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <p>Ingen data att visa för vald filtrering.</p>
          )}
        </div>
      ) : (
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead
              style={{ position: 'sticky', top: 0, backgroundColor: 'white' }}
            >
              <tr>
                <th
                  onClick={() => handleSort('groupName')}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    textAlign: 'left',
                    borderBottom: '2px solid #ddd',
                  }}
                >
                  {groupLabel}{' '}
                  <SortIndicator
                    field="groupName"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('totalCases')}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    textAlign: 'right',
                    borderBottom: '2px solid #ddd',
                  }}
                >
                  Totalt{' '}
                  <SortIndicator
                    field="totalCases"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('deviationCases')}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    textAlign: 'right',
                    borderBottom: '2px solid #ddd',
                  }}
                >
                  Avvik{' '}
                  <SortIndicator
                    field="deviationCases"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
                <th
                  onClick={() => handleSort('deviationPercentage')}
                  style={{
                    cursor: 'pointer',
                    padding: '8px',
                    textAlign: 'right',
                    borderBottom: '2px solid #ddd',
                  }}
                >
                  Andel{' '}
                  <SortIndicator
                    field="deviationPercentage"
                    sortField={sortField}
                    sortDirection={sortDirection}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((dp, index) => (
                <tr
                  key={dp.groupName || index}
                  style={{ borderBottom: '1px solid #eee' }}
                >
                  <td style={{ padding: '8px' }}>
                    {dp.groupName || '(Ingen)'}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    {dp.totalCases}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    {dp.deviationCases}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'right' }}>
                    {dp.deviationPercentage.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Widget>
  );
};

export default DeviationJobsWidget;
